import apiClient from './apiService';
import type { AuditRecord } from '../components/common/AuditHistoryModal';

export interface ImpactCategory {
    id: number;
    entity_id: number;
    impact_category_name: string;
    is_active: number;
    is_approved: number;
    created_by?: number;
    created_on?: string;
    modified_by?: number | null;
    modified_on?: string | null;
    // Approval workflow (maker-checker)
    workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
    submitted_by?: number;
    submitted_on?: string;
    approved_by?: number;
    approved_on?: string;
    approval_note?: string;
    rejected_by?: number;
    rejected_on?: string;
    rejection_reason?: string;
}

export interface ImpactCategoryCreate {
    impact_category_name: string;
}

export interface ImpactCategoryUpdate {
    impact_category_name?: string;
    is_active?: number;
}

const BASE = '/api/v1/cap/impact-categories';

export const capaImpactCategoriesService = {
    list: async (): Promise<ImpactCategory[]> => {
        const resp = await apiClient.get(BASE);
        return resp.data?.data ?? [];
    },
    /**
     * Approved + active categories for pickers. Hits the public /dropdown
     * endpoint (no CAP_MASTERS permission), so staff who cannot manage the
     * masters list can still pick a category when creating an impact.
     */
    dropdown: async (): Promise<ImpactCategory[]> => {
        const resp = await apiClient.get(`${BASE}/dropdown`);
        const items = resp.data?.items ?? [];
        return items.map((i: any) => ({ id: i.id, impact_category_name: i.name } as ImpactCategory));
    },
    create: async (data: ImpactCategoryCreate): Promise<ImpactCategory> => {
        const resp = await apiClient.post(BASE, data);
        return resp.data?.data;
    },
    update: async (id: number, data: ImpactCategoryUpdate): Promise<ImpactCategory> => {
        const resp = await apiClient.put(`${BASE}/${id}`, data);
        return resp.data?.data;
    },
    remove: async (id: number): Promise<void> => {
        await apiClient.delete(`${BASE}/${id}`);
    },

    /** Submit a draft/rejected record for approval. */
    submit: async (id: number): Promise<void> => {
        const resp = await apiClient.post(`${BASE}/${id}/submit`);
        if (resp.data && resp.data.success === false) throw new Error(resp.data.error || 'Submit failed');
    },
    /** Approve a submitted record (approver scope only). */
    approve: async (id: number, note?: string): Promise<void> => {
        const resp = await apiClient.post(`${BASE}/${id}/approve`, { note });
        if (resp.data && resp.data.success === false) throw new Error(resp.data.error || 'Approve failed');
    },
    /** Reject a submitted record with mandatory reason (approver scope only). */
    reject: async (id: number, reason: string): Promise<void> => {
        const resp = await apiClient.post(`${BASE}/${id}/reject`, { reason });
        if (resp.data && resp.data.success === false) throw new Error(resp.data.error || 'Reject failed');
    },
    /** Reopen an approved/rejected record back to draft (approver scope only). */
    reopen: async (id: number): Promise<void> => {
        const resp = await apiClient.post(`${BASE}/${id}/reopen`);
        if (resp.data && resp.data.success === false) throw new Error(resp.data.error || 'Reopen failed');
    },
    /** Fetch audit/workflow history for an impact category. */
    getHistory: async (id: number): Promise<AuditRecord[]> => {
        const resp = await apiClient.get(`${BASE}/${id}/history`);
        if (resp.data?.success && resp.data?.data) {
            return (resp.data.data as any[]).map((r: any) => ({
                audit_id: r.id,
                action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
                changed_by_username: r.changed_by_username || 'Unknown',
                changed_on: r.changed_on,
                change_reason: r.remarks,
            }));
        }
        throw new Error(resp.data?.error || 'Failed to fetch history');
    },
};
